import { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Trash2, Save, Sparkles, SlidersHorizontal } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';

type Skill = { id: number; name: string; sequence: number; is_active: number };
type Band = { label: string; grade_point: string | number | null; min_percent: string | number | null; max_percent: string | number | null };
type Scale = { id: number; name: string; scale_type: 'marks' | 'descriptive'; is_default: number; bands: Band[] };

type Section = 'skills' | 'advanced';

export default function AssessmentSchemePage() {
  const [section, setSection] = useState<Section>('skills');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div className="page-head">
        <div className="page-head-icon"><ClipboardList size={24} style={{ color: '#fff' }} /></div>
        <div className="page-head-text">
          <h1>Report Card Settings</h1>
          <p>Manage co-scholastic skills and (advanced) the letter-grade scale</p>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <nav className="side-menu">
          {([
            ['skills', 'Skills', 'Handwriting, Discipline…', Sparkles],
            ['advanced', 'Grade Scale', 'Advanced — marks → letter', SlidersHorizontal],
          ] as [Section, string, string, typeof Sparkles][]).map(([key, label, desc, Icon]) => (
            <button key={key} onClick={() => setSection(key)} className={`side-menu-item${section === key ? ' active' : ''}`}>
              <Icon size={18} className="smi-icon" />
              <span className="smi-txt">
                <span className="smi-l">{label}</span>
                <span className="smi-d">{desc}</span>
              </span>
            </button>
          ))}
        </nav>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {section === 'skills' && <SkillsSection />}
          {section === 'advanced' && <AdvancedSection />}
        </div>
      </div>
    </div>
  );
}

// ─── Co-scholastic skills ─────────────────────────────────────────────────────

function SkillsSection() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    apiRequest<any>(`${BASE}/scheme/skills`)
      .then(res => setSkills(res.data || []))
      .catch(() => toastMessage('Failed to load skills', 'error'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim()) return toastMessage('Enter a skill name', 'error');
    try {
      await apiRequest(`${BASE}/scheme/skills`, { method: 'POST', body: JSON.stringify({ name: name.trim() }) });
      setName(''); load(); toastMessage('Skill added', 'success');
    } catch { toastMessage('Could not add (maybe a duplicate name)', 'error'); }
  };
  const remove = async (id: number) => {
    if (!confirm('Delete this skill?')) return;
    try { await apiRequest(`${BASE}/scheme/skills/${id}`, { method: 'DELETE' }); load(); }
    catch { toastMessage('Could not delete', 'error'); }
  };

  if (loading) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 560 }}>
      <p style={{ color: 'var(--text-2)', fontSize: 13.5, marginBottom: 16, lineHeight: 1.5 }}>
        Skills are non-academic traits graded <b>A / B / C</b> (e.g. Handwriting, Discipline, Teamwork). They're optional —
        when you set up an exam you tick which skills to include. Subjects come from the class itself, so there's nothing to set up here for them.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <input className="input" placeholder="Skill name (e.g. Handwriting)" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }} style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={add} style={{ gap: 6 }}><Plus size={16} /> Add</button>
      </div>
      {skills.length === 0
        ? <div className="empty"><div className="empty-art" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}><Sparkles size={34} /></div><p>No skills yet</p></div>
        : skills.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8 }}>
            <Sparkles size={16} style={{ color: 'var(--text-3)' }} />
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{s.name}</span>
            <button className="icon-btn" onClick={() => remove(s.id)} style={{ marginLeft: 'auto' }}><Trash2 size={16} /></button>
          </div>
        ))}
    </div>
  );
}

// ─── Advanced: grade scale bands ──────────────────────────────────────────────

function AdvancedSection() {
  const [scales, setScales] = useState<Scale[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    apiRequest<any>(`${BASE}/scheme/grade-scales`)
      .then(res => setScales(res.data || []))
      .catch(() => toastMessage('Failed to load scales', 'error'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (loading) return <p style={{ color: 'var(--text-2)' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 720 }}>
      <p style={{ color: 'var(--text-2)', fontSize: 13.5, marginBottom: 16, lineHeight: 1.5 }}>
        These control how marks become letter grades on the report card. The defaults follow the common 10-point scheme —
        most schools never need to change them.
      </p>
      {scales.map(s => <ScaleCard key={s.id} scale={s} onSaved={load} />)}
    </div>
  );
}

function ScaleCard({ scale, onSaved }: { scale: Scale; onSaved: () => void }) {
  const [bands, setBands] = useState<Band[]>(scale.bands.length ? scale.bands : [{ label: '', grade_point: '', min_percent: '', max_percent: '' }]);
  const isMarks = scale.scale_type === 'marks';

  const setBand = (i: number, p: Partial<Band>) => setBands(bs => bs.map((b, j) => j === i ? { ...b, ...p } : b));
  const addBand = () => setBands(bs => [...bs, { label: '', grade_point: '', min_percent: '', max_percent: '' }]);
  const delBand = (i: number) => setBands(bs => bs.filter((_, j) => j !== i));

  const save = async () => {
    try {
      await apiRequest(`${BASE}/scheme/grade-scales/${scale.id}/bands`, {
        method: 'PUT',
        body: JSON.stringify({ bands: bands.filter(b => b.label.trim()).map(b => ({
          label: b.label.trim(),
          grade_point: b.grade_point === '' ? null : Number(b.grade_point),
          min_percent: isMarks ? (b.min_percent === '' ? null : Number(b.min_percent)) : null,
          max_percent: isMarks ? (b.max_percent === '' ? null : Number(b.max_percent)) : null,
        })) }),
      });
      onSaved(); toastMessage('Saved', 'success');
    } catch { toastMessage('Could not save', 'error'); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontWeight: 800, fontSize: 14 }}>{scale.name}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'var(--slate-soft)', color: 'var(--text-2)' }}>
          {isMarks ? 'Subjects' : 'Skills'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMarks ? '1fr 1fr 1fr 1fr auto' : '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
        <b style={hcell}>Grade</b>
        {isMarks && <b style={hcell}>Min %</b>}
        {isMarks && <b style={hcell}>Max %</b>}
        <b style={hcell}>Points</b>
        <span />
        {bands.map((b, i) => (
          <FragmentRow key={i}>
            <input className="input" placeholder="A1" value={b.label} onChange={e => setBand(i, { label: e.target.value })} />
            {isMarks && <input className="input" type="number" placeholder="91" value={b.min_percent ?? ''} onChange={e => setBand(i, { min_percent: e.target.value })} />}
            {isMarks && <input className="input" type="number" placeholder="100" value={b.max_percent ?? ''} onChange={e => setBand(i, { max_percent: e.target.value })} />}
            <input className="input" type="number" step="0.1" placeholder="10" value={b.grade_point ?? ''} onChange={e => setBand(i, { grade_point: e.target.value })} />
            <button className="icon-btn" onClick={() => delBand(i)}><Trash2 size={14} /></button>
          </FragmentRow>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-ghost" onClick={addBand} style={{ gap: 6 }}><Plus size={14} /> Add row</button>
        <button className="btn btn-primary" onClick={save} style={{ gap: 6, marginLeft: 'auto' }}><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) { return <>{children}</>; }

const hcell: React.CSSProperties = { fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3 };
